// src/utils/Onboarding/onboardingState.js

import { createInitialFieldsState, createEmptyFieldState, getOnboardingFieldDefinition, ONBOARDING_FIELD_KEYS } from './onboardingFields.js';
import { generateOnboardingSessionId, getUtcIsoTimestamp } from './session.js';

const FILLED_ONCE = 'filled_once';
const UPDATED_MULTI = 'updated_multiple_times';

function deriveStatus(fillCount) {
  if (fillCount <= 0) return 'never_filled';
  if (fillCount === 1) return FILLED_ONCE;
  return UPDATED_MULTI;
}

export function createInitialOnboardingState(options = {}) {
  const createdAt = options.createdAt ?? getUtcIsoTimestamp();
  const sessionId = options.sessionId ?? generateOnboardingSessionId(createdAt);
  const fields = createInitialFieldsState();
  const latestValues = {};

  for (const key of ONBOARDING_FIELD_KEYS) {
    const field = fields[key];
    latestValues[key] = field.latestValue;
  }

  return {
    session: {
      id: sessionId,
      createdAt,
      lastUpdatedAt: createdAt,
    },
    fields,
    latestValues,
    dirtyFields: {},
    hasPendingChanges: false,
    sync: {
      lastSyncedAt: null,
      lastSyncAttemptAt: null,
      lastSyncError: null,
      firestoreDocumentId: null,
    },
  };
}

export function ensureFieldState(state, fieldKey) {
  if (state.fields[fieldKey]) return state.fields[fieldKey];
  const definition = getOnboardingFieldDefinition(fieldKey);
  if (!definition) return null;
  const cloned = createEmptyFieldState(fieldKey);
  state.fields[fieldKey] = cloned;
  state.latestValues[fieldKey] = cloned.latestValue;
  return cloned;
}

export function applyFieldUpdates(state, updates = {}, options = {}) {
  if (!state) {
    throw new Error('applyFieldUpdates requires an initialized onboarding state.');
  }
  if (!updates || typeof updates !== 'object') {
    return state;
  }

  const fieldEntries = Object.entries(updates);
  if (fieldEntries.length === 0) {
    return state;
  }

  const perFieldOptions = options.perField ?? {};
  const baseTimestamp = options.timestamp ?? getUtcIsoTimestamp();
  const baseSource = options.source ?? 'local';

  let hasChanges = false;
  const fields = { ...state.fields };
  const latestValues = { ...state.latestValues };
  const dirtyFields = { ...(state.dirtyFields || {}) };
  let sessionUpdatedAt = state.session?.lastUpdatedAt ?? state.session?.createdAt ?? baseTimestamp;

  for (const [fieldKey, rawValue] of fieldEntries) {
    const definition = getOnboardingFieldDefinition(fieldKey);
    if (!definition) {
      continue;
    }

    const fieldOption = perFieldOptions[fieldKey] || {};
    const entryTimestamp = fieldOption.timestamp ?? baseTimestamp;
    const entrySource = fieldOption.source ?? baseSource;
    const entryMetadata = fieldOption.metadata ?? options.metadata ?? null;

    const previousField = fields[fieldKey] ?? createEmptyFieldState(fieldKey);

    const history = [...previousField.history, {
      value: rawValue,
      updatedAt: entryTimestamp,
      source: entrySource,
      metadata: entryMetadata,
    }];

    const fillCount = history.length;
    const status = deriveStatus(fillCount);
    const firstFilledAt = previousField.firstFilledAt ?? entryTimestamp;

    fields[fieldKey] = {
      ...previousField,
      history,
      latestValue: rawValue,
      latestUpdatedAt: entryTimestamp,
      lastValueSource: entrySource,
      firstFilledAt,
      fillCount,
      status,
    };

    latestValues[fieldKey] = rawValue;
    dirtyFields[fieldKey] = true;
    hasChanges = true;

    if (!sessionUpdatedAt || entryTimestamp > sessionUpdatedAt) {
      sessionUpdatedAt = entryTimestamp;
    }
  }

  if (!hasChanges) {
    return state;
  }

  return {
    ...state,
    session: {
      ...state.session,
      lastUpdatedAt: sessionUpdatedAt,
    },
    fields,
    latestValues,
    dirtyFields,
    hasPendingChanges: true,
    sync: {
      ...state.sync,
      lastSyncError: state.sync?.lastSyncError ?? null,
    },
  };
}

export function getLatestOnboardingValues(state) {
  if (!state) return {};
  return { ...state.latestValues };
}

export function getFieldHistory(state, fieldKey) {
  if (!state) return [];
  const field = state.fields?.[fieldKey];
  return field ? [...field.history] : [];
}

export function markFieldsAsSynced(state, fieldKeys = null, options = {}) {
  if (!state) return state;
  const keysToClear = Array.isArray(fieldKeys) && fieldKeys.length > 0
    ? fieldKeys
    : Object.keys(state.dirtyFields || {});

  const dirtyFields = { ...(state.dirtyFields || {}) };
  for (const key of keysToClear) {
    delete dirtyFields[key];
  }

  const hasPendingChanges = Object.keys(dirtyFields).length > 0;
  const lastSyncedAt = options.lastSyncedAt ?? getUtcIsoTimestamp();
  const syncPatch = {
    ...state.sync,
    lastSyncedAt,
    lastSyncError: options.lastSyncError ?? null,
    lastSyncAttemptAt: options.lastSyncAttemptAt ?? lastSyncedAt,
    firestoreDocumentId: options.firestoreDocumentId ?? state.sync?.firestoreDocumentId ?? null,
  };

  return {
    ...state,
    dirtyFields,
    hasPendingChanges,
    sync: syncPatch,
  };
}

export function recordSyncError(state, error, options = {}) {
  if (!state) return state;
  const lastSyncAttemptAt = options.lastSyncAttemptAt ?? getUtcIsoTimestamp();
  return {
    ...state,
    sync: {
      ...state.sync,
      lastSyncError: error instanceof Error ? error.message : error ?? 'Unknown sync error',
      lastSyncAttemptAt,
    },
  };
}
